import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  PLOT_PALETTE,
  VectorPlot,
} from "@/components/tools/linear-algebra/vector-plot";
import type { VECTOR_CALCULATOR_STRINGS } from "@/components/tools/vector-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { AngleUnit } from "@/lib/tool-kit/linear-algebra/angle-input";
import { piMultiple } from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  formatQuad,
  type QuadNumber,
  quadToComplex,
  sqrtRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  addVec,
  angleBetween,
  cross2D,
  crossProduct,
  dotProduct,
  isZeroVec,
  magnitude,
  normSquared,
  projectOnto,
  scaleVec,
  subVec,
  unitVector,
  type Vec,
} from "@/lib/tool-kit/linear-algebra/vector";
import {
  formatStyled,
  isZeroR,
  mulR,
  type NumberStyle,
  type Rational,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof VECTOR_CALCULATOR_STRINGS;

export interface VectorCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  unit: AngleUnit;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const NEEDS_BRACKETS = /^−|\/|\s/;

export function val(ctx: VectorCtx, x: Rational): string {
  return formatStyled(x, ctx.style, ctx.mark);
}

export function br(ctx: VectorCtx, x: Rational): string {
  const text = val(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

export function quad(ctx: VectorCtx, x: QuadNumber): string {
  return formatQuad(x, ctx.style, ctx.mark);
}

function brQuad(ctx: VectorCtx, x: QuadNumber): string {
  const text = quad(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

export function vecText(parts: string[]): string {
  return `(${parts.join(", ")})`;
}

export function decimalText(ctx: VectorCtx, x: QuadNumber): string {
  return ctx.fmt(quadToComplex(x).re, { maximumFractionDigits: 6 });
}

/** An angle written in the reader's chosen unit first, with the other in brackets. */
export function angleText(
  ctx: VectorCtx,
  degrees: number,
  exactDegrees: number | null
): string {
  const { strings } = ctx;
  const deg = (value: string) => t(strings.degreesUnit, { value });
  const rad = (value: string) => t(strings.radiansUnit, { value });
  const radians = (degrees * Math.PI) / 180;
  if (exactDegrees !== null) {
    const d = deg(ctx.fmt(exactDegrees));
    const r = rad(piMultiple(exactDegrees));
    return ctx.unit === "degrees" ? `${d} = ${r}` : `${r} = ${d}`;
  }
  const d = deg(ctx.fmt(degrees, { maximumFractionDigits: 4 }));
  const r = rad(ctx.fmt(radians, { maximumFractionDigits: 6 }));
  return ctx.unit === "degrees" ? `${d} ≈ ${r}` : `${r} ≈ ${d}`;
}

interface Row {
  id: string;
  label: string;
  working: string;
  result: string;
}

function pairs(ctx: VectorCtx, u: Vec, v: Vec, sign: "+" | "−"): string {
  return vecText(u.map((x, i) => `${br(ctx, x)} ${sign} ${br(ctx, v[i])}`));
}

function magnitudeWorking(ctx: VectorCtx, vec: Vec, name: string): string {
  const squares = vec.map((x) => `${br(ctx, x)}²`).join(" + ");
  return `|${name}| = √(${squares}) = √${br(ctx, normSquared(vec))}`;
}

function dotWorking(ctx: VectorCtx, u: Vec, v: Vec): string {
  const terms = u.map((x, i) => `${br(ctx, x)} × ${br(ctx, v[i])}`).join(" + ");
  return `u · v = ${terms}`;
}

function crossWorking(ctx: VectorCtx, u: Vec, v: Vec): string {
  if (u.length === 2) {
    return `u × v = ${br(ctx, u[0])} × ${br(ctx, v[1])} − ${br(ctx, u[1])} × ${br(ctx, v[0])}`;
  }
  const part = (a: number, b: number) =>
    `${br(ctx, u[a])} × ${br(ctx, v[b])} − ${br(ctx, u[b])} × ${br(ctx, v[a])}`;
  return `u × v = (${part(1, 2)},  ${part(2, 0)},  ${part(0, 1)})`;
}

function arithmeticRows(ctx: VectorCtx, u: Vec, v: Vec, k: Rational): Row[] {
  const { strings } = ctx;
  const list = (vec: Vec) => vecText(vec.map((x) => val(ctx, x)));
  return [
    {
      id: "add",
      label: strings.rowLabels.add,
      working: pairs(ctx, u, v, "+"),
      result: list(addVec(u, v)),
    },
    {
      id: "sub",
      label: strings.rowLabels.subtract,
      working: pairs(ctx, u, v, "−"),
      result: list(subVec(u, v)),
    },
    {
      id: "ku",
      label: t(strings.rowLabels.scaleU, { k: val(ctx, k) }),
      working: vecText(u.map((x) => `${br(ctx, k)} × ${br(ctx, x)}`)),
      result: list(scaleVec(u, k)),
    },
    {
      id: "kv",
      label: t(strings.rowLabels.scaleV, { k: val(ctx, k) }),
      working: vecText(v.map((x) => `${br(ctx, k)} × ${br(ctx, x)}`)),
      result: list(scaleVec(v, k)),
    },
  ];
}

function lengthRows(ctx: VectorCtx, u: Vec, v: Vec): Row[] {
  const { strings } = ctx;
  const rows: Row[] = [];
  for (const [name, vec] of [
    ["u", u],
    ["v", v],
  ] as const) {
    const mag = magnitude(vec);
    const approx = isZeroR(mag.b) ? "" : ` ≈ ${decimalText(ctx, mag)}`;
    rows.push({
      id: `mag-${name}`,
      label: t(strings.rowLabels.magnitude, { name }),
      working: magnitudeWorking(ctx, vec, name),
      result: `${quad(ctx, mag)}${approx}`,
    });
    const unit = unitVector(vec);
    rows.push({
      id: `unit-${name}`,
      label: t(strings.rowLabels.unit, { name }),
      working: t(strings.unitWorking, { name }),
      result: unit
        ? vecText(unit.map((x) => quad(ctx, x)))
        : strings.zeroVector,
    });
  }
  return rows;
}

function productRows(ctx: VectorCtx, u: Vec, v: Vec): Row[] {
  const { strings } = ctx;
  const dot = dotProduct(u, v);
  const rows: Row[] = [
    {
      id: "dot",
      label: strings.rowLabels.dot,
      working: dotWorking(ctx, u, v),
      result: val(ctx, dot),
    },
  ];
  if (u.length === 3) {
    rows.push({
      id: "cross",
      label: strings.rowLabels.cross,
      working: crossWorking(ctx, u, v),
      result: vecText(crossProduct(u, v).map((x) => val(ctx, x))),
    });
  } else {
    rows.push({
      id: "cross2",
      label: strings.rowLabels.cross2D,
      working: crossWorking(ctx, u, v),
      result: val(ctx, cross2D(u, v)),
    });
  }
  const area = sqrtRational(
    subR(mulR(normSquared(u), normSquared(v)), mulR(dot, dot))
  );
  rows.push({
    id: "area",
    label: strings.rowLabels.area,
    working: strings.areaWorking,
    result: `${quad(ctx, area)}${isZeroR(area.b) ? "" : ` ≈ ${decimalText(ctx, area)}`}`,
  });
  return rows;
}

function angleRow(ctx: VectorCtx, u: Vec, v: Vec): Row {
  const { strings } = ctx;
  const angle = angleBetween(u, v);
  if (angle === null) {
    return {
      id: "angle",
      label: strings.rowLabels.angle,
      working: strings.angleZero,
      result: "—",
    };
  }
  return {
    id: "angle",
    label: strings.rowLabels.angle,
    working: t(strings.angleWorking, {
      dot: val(ctx, dotProduct(u, v)),
      magU: brQuad(ctx, magnitude(u)),
      magV: brQuad(ctx, magnitude(v)),
      cos: quad(ctx, angle.cos),
    }),
    result: angleText(ctx, angle.degrees, angle.exactDegrees),
  };
}

function projectionRows(ctx: VectorCtx, u: Vec, v: Vec): Row[] {
  const { strings } = ctx;
  const rows: Row[] = [];
  for (const [onto, from, a, b] of [
    ["v", "u", u, v],
    ["u", "v", v, u],
  ] as const) {
    const projection = projectOnto(a, b);
    rows.push({
      id: `proj-${onto}`,
      label: t(strings.rowLabels.projection, { from, onto }),
      working: t(strings.projectionWorking, {
        from,
        onto,
        factor: projection ? val(ctx, projection.factor) : "",
      }),
      result: projection
        ? vecText(projection.vector.map((x) => val(ctx, x)))
        : strings.zeroVector,
    });
    rows.push({
      id: `scalar-${onto}`,
      label: t(strings.rowLabels.scalarProjection, { from, onto }),
      working: t(strings.scalarWorking, { from, onto }),
      result: projection
        ? `${quad(ctx, projection.scalar)}${isZeroR(projection.scalar.b) ? "" : ` ≈ ${decimalText(ctx, projection.scalar)}`}`
        : strings.zeroVector,
    });
  }
  return rows;
}

function Plot({ u, v, ctx }: { u: Vec; v: Vec; ctx: VectorCtx }) {
  const { strings } = ctx;
  const [ux, uy, vx, vy] = [u[0], u[1], v[0], v[1]].map(toNumberR);
  const projection = projectOnto(u, v);
  const foot = projection
    ? { x: toNumberR(projection.vector[0]), y: toNumberR(projection.vector[1]) }
    : null;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <VectorPlot
        arrows={[
          { x: ux, y: uy, color: PLOT_PALETTE[0], label: "u" },
          { x: vx, y: vy, color: PLOT_PALETTE[1], label: "v" },
          {
            x: ux + vx,
            y: uy + vy,
            color: PLOT_PALETTE[2],
            label: "u + v",
            dashed: true,
          },
          ...(foot && (foot.x !== 0 || foot.y !== 0)
            ? [{ ...foot, color: PLOT_PALETTE[5], label: "proj" }]
            : []),
        ]}
        downloadLabel={strings.downloadPng}
        fileName="vectors.png"
        segments={[
          {
            from: { x: ux, y: uy },
            to: { x: ux + vx, y: uy + vy },
            color: PLOT_PALETTE[1],
          },
          {
            from: { x: vx, y: vy },
            to: { x: ux + vx, y: uy + vy },
            color: PLOT_PALETTE[0],
          },
          ...(foot
            ? [{ from: { x: ux, y: uy }, to: foot, color: PLOT_PALETTE[5] }]
            : []),
        ]}
        title={strings.plotTitle}
      />
      <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
    </>
  );
}

export function TwoVectorResults({
  u,
  v,
  k,
  ctx,
}: {
  u: Vec;
  v: Vec;
  k: Rational;
  ctx: VectorCtx;
}) {
  const { strings } = ctx;
  const dot = dotProduct(u, v);
  const angle = angleBetween(u, v);
  const rows = [
    ...arithmeticRows(ctx, u, v, k),
    ...lengthRows(ctx, u, v),
    ...productRows(ctx, u, v),
    angleRow(ctx, u, v),
    ...projectionRows(ctx, u, v),
  ];
  const parallel =
    !(isZeroVec(u) || isZeroVec(v)) &&
    isZeroR(subR(mulR(normSquared(u), normSquared(v)), mulR(dot, dot)));
  let relation = "";
  if (!(isZeroVec(u) || isZeroVec(v))) {
    if (isZeroR(dot)) {
      relation = strings.orthogonal;
    } else if (parallel) {
      relation = strings.parallel;
    }
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.dotLabel} value={val(ctx, dot)} />
        <StatCard
          label={t(strings.rowLabels.magnitude, { name: "u" })}
          value={quad(ctx, magnitude(u))}
        />
        <StatCard
          label={t(strings.rowLabels.magnitude, { name: "v" })}
          value={quad(ctx, magnitude(v))}
        />
        <StatCard
          label={strings.rowLabels.angle}
          value={
            angle ? angleText(ctx, angle.degrees, angle.exactDegrees) : "—"
          }
        />
      </StatGrid>
      {relation === "" ? null : (
        <p className={`${RESULT_CARD} text-sm`}>{relation}</p>
      )}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-left">
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr className="align-top" key={row.id}>
                <th
                  className="w-48 p-3 font-medium text-foreground text-sm"
                  scope="row"
                >
                  {row.label}
                </th>
                <td className="space-y-1 p-3">
                  <span className="block break-words font-mono text-muted-foreground text-xs">
                    {row.working}
                  </span>
                  <span className="block break-words font-mono font-semibold text-foreground text-sm">
                    {row.result}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={rows.map((row) => `${row.label}: ${row.result}`).join("\n")}
      />
      {u.length === 2 ? (
        <Plot ctx={ctx} u={u} v={v} />
      ) : (
        <p className="text-muted-foreground text-sm">{strings.noPlot3d}</p>
      )}
    </>
  );
}
